import React from "react";
import { Box, Grid2, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  formatDate,
  formatSeconds,
  parseUtcDate,
} from "../../../../../../views/components/common/functions/formatDate";

interface SectionStageTimingProps {
  firstLabel: string;
  lastLabel: string;
  firstAt?: string | Date | null;
  lastAt?: string | Date | null;
  /** When provided, used as Time spent instead of last − first. */
  cumulativeSeconds?: number | null;
}

const getElapsedSeconds = (
  startAt?: string | Date | null,
  endAt?: string | Date | null,
): number | null => {
  if (!startAt) return null;
  try {
    const startTs = parseUtcDate(startAt)?.getTime();
    const endTs = endAt ? parseUtcDate(endAt)?.getTime() : Date.now();
    if (startTs == null || endTs == null || Number.isNaN(startTs) || Number.isNaN(endTs)) {
      return null;
    }
    return Math.max(0, Math.floor((endTs - startTs) / 1000));
  } catch {
    return null;
  }
};

const SectionStageTiming: React.FC<SectionStageTimingProps> = ({
  firstLabel,
  lastLabel,
  firstAt,
  lastAt,
  cumulativeSeconds,
}) => {
  const theme = useTheme();
  const hasCumulative =
    cumulativeSeconds != null && Number.isFinite(Number(cumulativeSeconds));
  const hasAny = Boolean(firstAt || lastAt || hasCumulative);

  if (!hasAny) return null;

  let durationValue = "—";
  if (hasCumulative) {
    durationValue = formatSeconds(Number(cumulativeSeconds));
  } else if (firstAt) {
    const elapsed = getElapsedSeconds(firstAt, lastAt ?? null);
    const formatted = formatSeconds(elapsed);
    durationValue =
      formatted !== "-" && !lastAt ? `${formatted} so far` : formatted;
  }

  const items = [
    {
      label: firstLabel,
      value: firstAt ? formatDate(firstAt) : "—",
    },
    {
      label: lastLabel,
      value: lastAt ? formatDate(lastAt) : "—",
    },
    {
      label: "Time spent",
      value: durationValue,
      emphasize: durationValue !== "—",
    },
  ];

  return (
    <Grid2 container spacing={1} sx={{ mb: 1.5 }}>
      {items.map((item) => (
        <Grid2 key={item.label} size={{ xs: 12, sm: 4 }}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
              borderRadius: "8px",
              backgroundColor: item.emphasize
                ? theme.palette.customColors.lightGreenBackground
                : theme.palette.customColors.greyBackground,
              px: 1.25,
              py: 0.75,
              height: "100%",
            }}
          >
            <CustomTypography
              variant="caption"
              color={theme.palette.customColors.greyTextColor}
            >
              {item.label}
            </CustomTypography>
            <CustomTypography
              variant="heading8"
              fontWeight={700}
              color={
                item.emphasize
                  ? theme.palette.customColors.EmeraldGreen
                  : undefined
              }
              style={{ marginTop: 4 }}
            >
              {item.value}
            </CustomTypography>
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
};

export default SectionStageTiming;
